/**
 * Guide de description d'une application (§3.2).
 *
 * Décrire une application à un modèle est un exercice que personne ne maîtrise
 * spontanément. On dit ce que fait l'application, et on oublie pour qui elle est,
 * ce qu'il advient des données au rechargement, à quoi doit ressembler un
 * graphique, ce qui se passe quand la liste est vide. Le modèle décide alors à la
 * place de l'auteur — et celui-ci ne l'apprend qu'en voyant le résultat.
 *
 * Ce guide pose ces questions, dans l'ordre où elles se posent, et compose la
 * description. Trois partis pris :
 *
 * 1. **Le texte reste visible et modifiable.** Le guide écrit la demande, il ne la
 *    cache pas derrière des cases : l'auteur voit exactement ce qui part au modèle,
 *    et peut le reprendre mot à mot. Un formulaire qui masque ce qu'il produit
 *    n'apprend rien à personne et plafonne à ce qu'il sait demander.
 * 2. **Rien n'est obligatoire sauf l'objet de l'application.** Le reste a un défaut
 *    raisonnable. Un questionnaire qui exige dix réponses avant de laisser passer
 *    se fait remplir n'importe comment.
 * 3. **Les diagrammes se choisissent à l'œil.** Voir la forme de ce qu'on demande
 *    règle en une seconde une hésitation que deux mots laissent entière.
 *
 * Le guide ne bride pas la complexité de l'application : il produit un point de
 * départ, et le champ « précisions » comme la reprise du texte accueillent tout ce
 * que le questionnaire n'a pas prévu.
 */

import clsx from 'clsx';
import {
  Check,
  Database,
  DatabaseZap,
  Info,
  ListChecks,
  PencilLine,
  Sparkles,
  Users,
  Wand2,
} from 'lucide-react';
import { useId, useMemo, useState } from 'react';

import {
  buildGuidedDescription,
  CHART_CATALOG,
  EMPTY_ANSWERS,
  FEATURE_CATALOG,
  isGuideUsable,
  type ChartKind,
  type FeatureKey,
  type GuideAnswers,
  type VisualTone,
} from '../lib/guided-description';
import { ChartThumbnail } from './ChartThumbnail';
import { Button, FOCUS_RING, Label } from './ui';

/** Champ de texte multiligne du guide, avec son aide sous le champ. */
function Zone({
  id,
  label,
  hint,
  placeholder,
  rows = 3,
  value,
  onChange,
  required,
  aide,
}: {
  id: string;
  label: string;
  hint?: string;
  placeholder: string;
  rows?: number;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  aide?: string;
}) {
  return (
    <div>
      <Label htmlFor={id} required={required} {...(hint ? { hint } : {})}>
        {label}
      </Label>
      <textarea
        id={id}
        value={value}
        rows={rows}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className={clsx(
          'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm',
          'text-slate-900 placeholder:text-slate-400',
          'dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100',
          FOCUS_RING,
        )}
      />
      {aide ? <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{aide}</p> : null}
    </div>
  );
}

/** Titre de section du guide, avec son icône et son propos. */
function Section({
  icone: Icone,
  titre,
  propos,
  children,
}: {
  icone: typeof Users;
  titre: string;
  propos: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3 border-t border-slate-200 pt-5 first:border-0 first:pt-0 dark:border-slate-800">
      <div className="flex items-start gap-2">
        <Icone
          className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600 dark:text-indigo-400"
          aria-hidden="true"
        />
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-50">{titre}</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">{propos}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

/** Case à cocher présentée comme une carte cliquable. */
function Carte({
  checked,
  onToggle,
  children,
  className,
}: {
  checked: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={onToggle}
      className={clsx(
        'relative rounded-lg border p-3 text-left transition-colors',
        FOCUS_RING,
        checked
          ? 'border-indigo-500 bg-indigo-50 dark:border-indigo-400 dark:bg-indigo-950'
          : 'border-slate-300 hover:border-slate-400 dark:border-slate-700 dark:hover:border-slate-600',
        className,
      )}
    >
      {checked ? (
        <Check
          className="absolute right-2 top-2 h-4 w-4 text-indigo-600 dark:text-indigo-400"
          aria-hidden="true"
        />
      ) : null}
      {children}
    </button>
  );
}

const TONES: ReadonlyArray<{ value: VisualTone; label: string; hint: string }> = [
  { value: 'sobre', label: 'Sobre', hint: 'peu de couleurs, beaucoup d’air' },
  { value: 'colore', label: 'Coloré', hint: 'couleurs franches, plus vivant' },
  { value: 'dense', label: 'Dense', hint: 'un maximum à l’écran, sans défilement' },
];

export interface GuidedFormProps {
  /** Description composée, remontée à chaque changement de réponse. */
  onChange: (description: string) => void;
  /**
   * Bascule vers l'édition libre du texte produit.
   *
   * Le guide devient alors inerte : deux sources modifiables pour un même texte
   * s'écraseraient l'une l'autre, et l'auteur perdrait sa rédaction au premier clic
   * sur une case.
   */
  onDetach: (description: string) => void;
}

/** Questionnaire de description, et aperçu du texte qu'il produit. */
export function GuidedForm({ onChange, onDetach }: GuidedFormProps) {
  const [answers, setAnswers] = useState<GuideAnswers>(EMPTY_ANSWERS);
  const [apercuOuvert, setApercuOuvert] = useState(false);

  const purposeId = useId();
  const audienceId = useId();
  const entitiesId = useId();
  const indicatorsId = useId();
  const extraId = useId();

  const description = useMemo(() => buildGuidedDescription(answers), [answers]);
  const utilisable = isGuideUsable(answers);

  /** Applique une réponse et remonte la description recomposée. */
  const repondre = <K extends keyof GuideAnswers>(cle: K, valeur: GuideAnswers[K]): void => {
    const suivant = { ...answers, [cle]: valeur };
    setAnswers(suivant);
    onChange(isGuideUsable(suivant) ? buildGuidedDescription(suivant) : '');
  };

  const basculer = <T,>(liste: readonly T[], valeur: T): T[] =>
    liste.includes(valeur) ? liste.filter((entree) => entree !== valeur) : [...liste, valeur];

  return (
    <div className="space-y-5">
      <Section
        icone={Sparkles}
        titre="Ce que fait l’application"
        propos="La seule réponse indispensable. Tout le reste a un défaut raisonnable."
      >
        <Zone
          id={purposeId}
          label="Objet de l’application"
          required
          rows={3}
          value={answers.purpose}
          onChange={(valeur) => repondre('purpose', valeur)}
          placeholder="Un suivi des demandes d’intervention de l’atelier : qui a demandé quoi, sur quelle machine, et où en est chaque demande."
          aide="Une ou deux phrases, comme vous l’expliqueriez à un collègue."
        />
      </Section>

      <Section
        icone={Users}
        titre="Pour qui"
        propos="Ce que le lecteur connaît déjà change le vocabulaire et le niveau de détail."
      >
        <Zone
          id={audienceId}
          label="Utilisateurs"
          rows={2}
          value={answers.audience}
          onChange={(valeur) => repondre('audience', valeur)}
          placeholder="Les techniciens de l’atelier, et le responsable de production pour le suivi."
        />
      </Section>

      <Section
        icone={ListChecks}
        titre="Les informations manipulées"
        propos="Une par ligne. C’est ce qui devient les colonnes d’un tableau ou les champs d’un formulaire."
      >
        <Zone
          id={entitiesId}
          label="Informations"
          rows={4}
          value={answers.entities}
          onChange={(valeur) => repondre('entities', valeur)}
          placeholder={
            'date de la demande\nmachine concernée\ndemandeur\nétat (à faire, en cours, terminé)'
          }
        />
      </Section>

      <Section
        icone={Database}
        titre="Les données doivent-elles être conservées ?"
        propos="C’est la question dont la réponse change le plus l’application."
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Carte
            checked={answers.dataPolicy === 'none'}
            onToggle={() => repondre('dataPolicy', 'none')}
          >
            <p className="pr-6 text-sm font-medium text-slate-900 dark:text-slate-50">
              Non, une session suffit
            </p>
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
              Tout repart de zéro au rechargement de la page. C’est ce qu’il faut pour un
              calculateur, une simulation, un outil qu’on ouvre puis qu’on ferme.
            </p>
            <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-400">
              L’application reste consultable par tous, sans connexion.
            </p>
          </Carte>

          <Carte
            checked={answers.dataPolicy === 'persist'}
            onToggle={() => repondre('dataPolicy', 'persist')}
          >
            <p className="pr-6 text-sm font-medium text-slate-900 dark:text-slate-50">
              Oui, elles doivent être gardées
            </p>
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
              Ce qui est saisi ou importé reste disponible plus tard, et pour les autres
              utilisateurs. Une base de données est attribuée à l’application.
            </p>
            {/*
              La contrepartie est annoncée ici, au moment du choix, et non découverte
              après le déploiement : elle décide de qui pourra ouvrir la page.
            */}
            <p className="mt-2 flex items-start gap-1 text-xs text-amber-700 dark:text-amber-400">
              <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
              L’application demandera une connexion à l’entreprise pour être ouverte.
            </p>
          </Carte>
        </div>
      </Section>

      <Section
        icone={DatabaseZap}
        titre="Des chiffres à suivre ?"
        propos="Si l’application présente des indicateurs, dites lesquels et sous quelle forme."
      >
        <Zone
          id={indicatorsId}
          label="Indicateurs"
          rows={2}
          value={answers.indicators}
          onChange={(valeur) => repondre('indicators', valeur)}
          placeholder="Nombre de demandes ouvertes, délai moyen de traitement, répartition par machine."
        />

        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Formes attendues — cliquez celles qui conviennent
          </p>
          {/* Les vignettes règlent en une seconde une hésitation que deux mots
              laissent entière : voir la forme demandée vaut mieux que l'imaginer. */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {CHART_CATALOG.map((entree) => (
              <Carte
                key={entree.kind}
                checked={answers.charts.includes(entree.kind)}
                onToggle={() =>
                  repondre('charts', basculer<ChartKind>(answers.charts, entree.kind))
                }
                className="flex flex-col gap-2"
              >
                <ChartThumbnail kind={entree.kind} className="h-10 w-full" />
                <span className="text-xs font-medium text-slate-900 dark:text-slate-50">
                  {entree.label}
                </span>
                <span className="text-[11px] leading-tight text-slate-500 dark:text-slate-400">
                  {entree.purpose}
                </span>
              </Carte>
            ))}
          </div>
        </div>
      </Section>

      <Section
        icone={Wand2}
        titre="Fonctions attendues"
        propos="Des attentes que tout le monde a, et que personne ne pense à écrire."
      >
        <div className="grid gap-2 sm:grid-cols-2">
          {FEATURE_CATALOG.map((entree) => (
            <Carte
              key={entree.key}
              checked={answers.features.includes(entree.key)}
              onToggle={() =>
                repondre('features', basculer<FeatureKey>(answers.features, entree.key))
              }
            >
              <p className="pr-6 text-sm font-medium text-slate-900 dark:text-slate-50">
                {entree.label}
              </p>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{entree.hint}</p>
            </Carte>
          ))}
        </div>
      </Section>

      <Section icone={PencilLine} titre="Présentation" propos="L’allure générale de l’écran.">
        <div className="flex flex-wrap gap-2">
          {TONES.map((entree) => (
            <Button
              key={entree.value}
              variant={answers.tone === entree.value ? 'primary' : 'secondary'}
              aria-pressed={answers.tone === entree.value}
              onClick={() => repondre('tone', entree.value)}
              className="flex-col items-start gap-0 py-2"
            >
              <span>{entree.label}</span>
              <span className="text-[11px] font-normal opacity-80">{entree.hint}</span>
            </Button>
          ))}
        </div>

        <Zone
          id={extraId}
          label="Précisions"
          rows={3}
          value={answers.extra}
          onChange={(valeur) => repondre('extra', valeur)}
          placeholder="Tout ce que les questions ci-dessus n’ont pas couvert : règles de calcul, cas particuliers, contraintes de couleur…"
          aide="Le guide ne bride pas ce que vous pouvez demander : ce champ accueille le reste."
        />
      </Section>

      {/* Ce qui part réellement au modèle, visible et reprenable. Un formulaire qui
          masque ce qu'il produit n'apprend rien et plafonne à ce qu'il sait demander. */}
      <section className="border-t border-slate-200 pt-5 dark:border-slate-800">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setApercuOuvert((ouvert) => !ouvert)}
            aria-expanded={apercuOuvert}
            className={clsx(
              'text-sm font-medium text-indigo-700 underline underline-offset-2 dark:text-indigo-300',
              FOCUS_RING,
            )}
          >
            {apercuOuvert ? 'Masquer' : 'Voir'} la demande envoyée à Claude
          </button>
          {utilisable ? (
            <Button onClick={() => onDetach(description)}>
              <PencilLine className="h-4 w-4" aria-hidden="true" />
              Reprendre le texte à la main
            </Button>
          ) : null}
        </div>

        {apercuOuvert ? (
          utilisable ? (
            <pre className="code-scroll mt-3 max-h-72 overflow-auto whitespace-pre-wrap rounded-md bg-slate-50 p-3 text-xs text-slate-700 dark:bg-slate-950 dark:text-slate-300">
              {description}
            </pre>
          ) : (
            <p className="mt-3 text-xs italic text-slate-500 dark:text-slate-400">
              Commencez par dire ce que fait l’application : la demande se composera au fur et à
              mesure de vos réponses.
            </p>
          )
        ) : null}
      </section>
    </div>
  );
}
